import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const csrImages = [
  {
    src: '/images/team/csr.jpg',
    alt: 'Community Development',
    caption: 'Supporting local communities through sustainable development initiatives'
  },
  {
    src: '/images/team/csr1.jpg',
    alt: 'Education Programs',
    caption: 'Empowering youth through education and training programs'
  },
  {
    src: '/images/team/csr2.jpg',
    alt: 'Environmental Conservation',
    caption: 'Promoting environmental conservation and sustainable practices'
  }
];

const teamBuildingImages = [
  {
    src: '/images/team/team4.jpg',
    alt: 'Team Building Activities',
    caption: 'Strengthening team bonds through collaborative activities'
  },
  {
    src: '/images/team/team5.jpg',
    alt: 'Team Workshops',
    caption: 'Enhancing skills through interactive workshops and training'
  },
  {
    src: '/images/team/team3.jpg',
    alt: 'Team Events',
    caption: 'Celebrating achievements and fostering team spirit'
  },
    {
      src: '/images/team/blue.jpg',
      alt: 'Team Workshops',
      caption: 'Team Blue'
    },
    {
      src: '/images/team/green.jpg',
      alt: 'Team Events',
      caption: 'Team Green'
    },
    {
      src: '/images/team/purple.jpg',
      alt: 'Team Workshops',
      caption: 'Team Purple'
    },
    {
      src: '/images/team/build.jpg',
      alt: 'Team Events',
      caption: 'Team Building'
    },
    {
      src: '/images/team/build1.jpg',
      alt: 'Team Workshops',
      caption: 'Team Building'
    },
    {
      src: '/images/team/build2.jpg',
      alt: 'Team Events',
      caption: 'Team Building'
    },
    {
      src: '/images/team/build3.jpg',
      alt: 'Team Workshops',
      caption: 'Team Building'
    },
];

const SocialCommunity = () => {
  const [currentCsrImage, setCurrentCsrImage] = useState(0);
  const [currentTeamImage, setCurrentTeamImage] = useState(0);

  useEffect(() => {
    const csrTimer = setInterval(() => {
      setCurrentCsrImage((prev) => (prev + 1) % csrImages.length);
    }, 5000);

    const teamTimer = setInterval(() => {
      setCurrentTeamImage((prev) => (prev + 1) % teamBuildingImages.length);
    }, 5000);

    return () => {
      clearInterval(csrTimer);
      clearInterval(teamTimer);
    };
  }, []);

  return (
    <Box>
      {/* Custom Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '160px', md: '180px' },
          paddingBottom: '60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/community.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            opacity: 0.7,
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Social & Community Initiatives
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
              }}
            >
              Making a positive impact through corporate social responsibility and team building
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* CSR Section */}
      <Box sx={{ py: 12, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h3" color="primary" gutterBottom>
                  Corporate Social Responsibility
                </Typography>
                <Typography variant="body1" paragraph>
                  At Geomaps, we believe in giving back to the communities we serve. Our CSR initiatives focus on:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    Supporting local communities through sustainable development projects
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Empowering youth through education and training programs
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Promoting environmental conservation and sustainable practices
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Contributing to social welfare and community development
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', height: '400px', borderRadius: 2, overflow: 'hidden' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentCsrImage}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 1 }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      src={csrImages[currentCsrImage].src}
                      alt={csrImages[currentCsrImage].alt}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontStyle: 'italic',
                          fontWeight: 700,
                        }}
                      >
                        {csrImages[currentCsrImage].caption}
                      </Typography>
                    </Box>
                  </motion.div>
                </AnimatePresence>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Team Building Section */}
      <Box sx={{ py: 12, backgroundColor: '#f8f9fa' }}>
        <Container>
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Box sx={{ position: 'relative', height: '400px', borderRadius: 2, overflow: 'hidden' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTeamImage}
                    initial={{ opacity: 0, x: -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 100 }}
                    transition={{ duration: 1 }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      src={teamBuildingImages[currentTeamImage].src}
                      alt={teamBuildingImages[currentTeamImage].alt}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontStyle: 'italic',
                          fontWeight: 700,
                        }}
                      >
                        {teamBuildingImages[currentTeamImage].caption}
                      </Typography>
                    </Box>
                  </motion.div>
                </AnimatePresence>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h3" color="primary" gutterBottom>
                  Team Building & Development
                </Typography>
                <Typography variant="body1" paragraph>
                  We invest in our team's growth and development through various initiatives:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    Regular team building activities and workshops
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Professional development and training programs
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Collaborative projects and cross-functional teams
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    Celebrating achievements and fostering team spirit
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default SocialCommunity; 